import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { GenericForm, GenericFormField } from '@/styles/GenericForm';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { useEntityComments } from '@/hooks/useEntityComments';
import { useKeyedAttachmentStaging } from '@/hooks/useKeyedAttachmentStaging';
import { fetchEntityAttachmentsByIds } from '@/lib/entity-attachments';
import { StandardButton } from '@/styles/StandardButtons';
import { PurchaseOrderCreateDialog } from '@/features/cost-management/purchase-orders/components/PurchaseOrderCreateDialog';
import { commonVendorOf, lineFromRow, toLinePayload } from '@/features/cost-management/purchase-orders/lib/po-lines';
import { fetchEntityList } from '@/lib/entity-api';
import { derivedDescription } from '@/lib/derived-description';
import { linesTotal, newLineKey, type LineItem } from '@/lib/line-items';
import { LinkedDocsSection } from '@/components/shared/LinkedDocsSection';
import { statusLabel } from '@/lib/status-label';
import { formatMoney } from '@/lib/line-items';
import { SelectionOptionsEditor, blankOption, type EditableOption } from './SelectionOptionsEditor';
import { SelectionOptionModal, serializeItems, type OptionItemsDraft } from './SelectionOptionModal';
import {
  saveSelection, saveSelectionOptionLines, setOptionSelected, uploadSelectionOptionAttachments, deleteSelectionOptionAttachment, isTempId,
  type Selection, type OptionDraft,
} from './store';

interface SelectionFormProps {
  mode: 'create' | 'edit';
  initialData?: Selection;
  projects: Array<{ id: string; name: string }>;
  /** Server says whether this caller may see/edit Builder Price. */
  canViewBuilderPrice: boolean;
  /** Called after a successful save; `failedFiles` counts staged files that could not be uploaded. */
  onSaved: (result: { selectionId: string; failedFiles: number }) => void;
  /** Called when an option is selected/deselected from inside the form (auto-saved) so the page can refresh. */
  onSelectionChanged: () => void;
  onCancel: () => void;
  isOpen: boolean;
}

const toMoney = (value: string) => (value === '' ? 0 : parseFloat(value) || 0);
let tempCounter = 0;
const newTempId = () => `temp-${Date.now()}-${tempCounter++}`;

export function SelectionForm({ mode, initialData, projects, canViewBuilderPrice, onSaved, onSelectionChanged, onCancel, isOpen }: SelectionFormProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const [options, setOptions] = useState<EditableOption[]>([]);
  const [removedOptionIds, setRemovedOptionIds] = useState<string[]>([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [togglingOptionId, setTogglingOptionId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousIsOpenRef = useRef(false);
  // The option open in its own modal, and every option's items (each option has its own): read when the Selection opens, saved with it
  const [openOptionId, setOpenOptionId] = useState<string | null>(null);
  const [itemsByOption, setItemsByOption] = useState<Record<string, OptionItemsDraft>>({});
  // the purchase order already made from this selection (at most one) and the one being made
  const [linkedPurchaseOrder, setLinkedPurchaseOrder] = useState<{ id: string; number: string; title?: string; status?: string; amount?: number } | null>(null);
  // Selection mode (Select / Copy / Delete of options): the checks only mark options for an action; they are not the approved state (is_selected)
  const [selecting, setSelecting] = useState(false);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [purchaseOrderStart, setPurchaseOrderStart] = useState<{ title: string; projectId: string; description: string; vendorId?: string; lines: LineItem[] } | null>(null);
  // The purchase order made from this selection is READ from the API (purchase_orders.selection_id): it is there after a reload, not only in this session's state
  const loadLinkedPurchaseOrder = useCallback(async (selectionId: string) => {
    try {
      const orders = await fetchEntityList<any>('purchase_order', { filters: { selection_id: selectionId } });
      setLinkedPurchaseOrder(orders[0] ? { id: orders[0].id, number: orders[0].po_number, title: orders[0].title, status: orders[0].status, amount: orders[0].amount } : null);
    } catch (err) {
      console.error('[SelectionForm] failed to load the purchase order of the selection', err);
    }
  }, []);
  const currentValuesRef = useRef<Record<string, any>>({});
  const snapshotRef = useRef('');

  const uploadFn = useCallback(async (optionId: string, files: File[]) => {
    if (!companyId || !user?.id) return [];
    return uploadSelectionOptionAttachments(optionId, files, companyId, user.id);
  }, [companyId, user?.id]);
  const deleteFn = useCallback((attachmentId: string) => deleteSelectionOptionAttachment(attachmentId, user?.id), [user?.id]);
  const staging = useKeyedAttachmentStaging({ uploadFn, deleteFn, onError: setError });
  const { seed, reset: resetStaging } = staging;

  const { comments, addComment, deleteComment } = useEntityComments('selection', mode === 'edit' ? initialData?.id : null);

  // Load the form's working state each time it opens.
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      setError(null);
      setRemovedOptionIds([]);
      resetStaging();
      const existing = mode === 'edit' && initialData ? initialData.options : [];
      setOptions(existing.map((o) => ({
        id: o.id,
        name: o.name,
        description: o.description || '',
        link: o.link || '',
        builder_price: o.builder_price ? String(o.builder_price) : '',
        client_price: o.client_price ? String(o.client_price) : '',
      })));
      setSelectedOptionIds(existing.filter((o) => o.is_selected).map((o) => o.id));
      setOpenOptionId(null);
      setItemsByOption({});
      setLinkedPurchaseOrder(null);
      setPurchaseOrderStart(null);
      setSelecting(false);
      setCheckedIds([]);
      snapshotRef.current = JSON.stringify(existing.map((o) => [o.id, o.name, o.description || '', o.link || '', Number(o.builder_price || 0), Number(o.client_price || 0)]));
      if (mode === 'edit' && initialData?.id) {
        // each option's own items (the table every document uses), and the purchase order made from this selection, if any
        existing.forEach((o) => {
          fetchEntityList<any>('selection_option_item', { filters: { selection_option_id: o.id } })
            .then((rows) => {
              const lines = [...rows].sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)).map((row) => lineFromRow(row, null));
              setItemsByOption((prev) => ({ ...prev, [o.id]: { lines, initial: serializeItems(lines) } }));
            })
            .catch((err) => console.error('[SelectionForm] failed to load the items of an option', err));
        });
        void loadLinkedPurchaseOrder(initialData.id);
      }
      if (existing.length > 0) {
        fetchEntityAttachmentsByIds('selection_option', existing.map((o) => o.id))
          .then((byOption) => existing.forEach((o) => seed(o.id, byOption.get(o.id) ?? [])))
          .catch((err) => console.error('[SelectionForm] failed to load option attachments', err));
      }
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, mode, initialData, resetStaging, seed, loadLinkedPurchaseOrder]);

  const initialValues = useMemo(() => {
    if (mode === 'edit' && initialData) {
      return {
        project_id: initialData.project_id || '',
        name: initialData.name || '',
        description: initialData.description || '',
        due_date: initialData.due_date || '',
        allowance: initialData.allowance ? String(initialData.allowance) : '',
        accept_multiple_selections: initialData.accept_multiple_selections === true,
      };
    }
    return {
      project_id: getDefaultProjectIdForNewRecord(selectedProject),
      name: '',
      description: '',
      due_date: '',
      allowance: '',
      accept_multiple_selections: false,
    };
  }, [mode, initialData, selectedProject]);

  const fields = useMemo<GenericFormField[]>(() => [
    { name: 'name', label: 'Title', type: 'text', required: true, row: 1, placeholder: 'Enter selection title' },
    { name: 'project_id', label: 'Project', type: 'select', required: true, row: 1, options: projects.map((p) => ({ label: p.name, value: p.id })), placeholder: 'Select' },
    { name: 'description', label: 'Description', type: 'textarea', row: 2, colSpan: 2, placeholder: 'Enter description' },
    // Due Date and Allowance share one row that takes half the width: each is a quarter, like the other short fields of the app's forms
    { name: 'due_date', label: 'Due Date', type: 'date', row: 3, rowMaxWidth: '50%' },
    { name: 'allowance', label: 'Allowance ($)', type: 'text', row: 3, placeholder: '$0.00' },
    { name: 'accept_multiple_selections', label: 'Approve multiple options', type: 'checkbox', row: 4, colSpan: 2 },
  ], [projects]);

  const handleFieldChange = (name: string, value: unknown, updated: Record<string, unknown>) => {
    if (name === 'allowance' && typeof value === 'string') updated.allowance = value.replace(/[^0-9.]/g, '');
    currentValuesRef.current = updated;
  };

  // A new option starts blank and opens in its own modal right away (its items need it to exist: they are saved with the Selection)
  const addOption = () => {
    const id = newTempId();
    setOptions((prev) => [...prev, blankOption(id)]);
    setItemsByOption((prev) => ({ ...prev, [id]: { lines: [], initial: '[]' } }));
    setOpenOptionId(id);
  };

  const removeOption = (optionId: string) => {
    setOptions((prev) => prev.filter((o) => o.id !== optionId));
    staging.discard(optionId);
    setItemsByOption((prev) => { const { [optionId]: _gone, ...rest } = prev; return rest; });
    if (!isTempId(optionId)) setRemovedOptionIds((prev) => [...prev, optionId]);
    setOpenOptionId((current) => (current === optionId ? null : current));
  };

  // Closing the modal of an option that was just added and never given a title leaves nothing behind
  const closeOptionModal = () => {
    const open = options.find((o) => o.id === openOptionId);
    if (open && isTempId(open.id) && !open.name.trim()) removeOption(open.id);
    setOpenOptionId(null);
  };

  const applyOption = (option: EditableOption, items: OptionItemsDraft) => {
    setOptions((prev) => prev.map((o) => (o.id === option.id ? option : o)));
    setItemsByOption((prev) => ({ ...prev, [option.id]: items }));
    setOpenOptionId(null);
  };

  // -- Select / Copy / Delete / Deselect of options ------------------------------------------------------------------------------------------
  const exitSelecting = () => { setSelecting(false); setCheckedIds([]); };
  const toggleChecked = (optionId: string, checked: boolean) => setCheckedIds((prev) => (checked ? Array.from(new Set([...prev, optionId])) : prev.filter((id) => id !== optionId)));

  // Copy: each checked option becomes a NEW option (new id, "(Copy)" after the title, not approved) with a copy of its fields and of its items; like any new option it is
  // created -- with its items -- when the Selection is saved. The files of the original are not copied.
  const copyChecked = () => {
    const sources = options.filter((o) => checkedIds.includes(o.id));
    if (sources.length === 0) return;
    if (sources.some((o) => !itemsByOption[o.id])) { setError('The items of an option are still loading. Try again in a moment.'); return; }
    const copies = sources.map((source) => ({ ...source, id: newTempId(), name: `${source.name} (Copy)` }));
    setOptions((prev) => [...prev, ...copies]);
    setItemsByOption((prev) => ({
      ...prev,
      ...Object.fromEntries(copies.map((copy, index) => [copy.id, { lines: (prev[sources[index].id]?.lines ?? []).map((line) => ({ ...line, key: newLineKey() })), initial: '[]' }])),
    }));
    setError(null);
    exitSelecting();
  };

  // Delete: after confirming. An option that exists is removed when the Selection is saved (like every edit here); it drops out of the approved ones at once so Create PO and the totals never count it.
  const deleteChecked = () => {
    const doomed = options.filter((o) => checkedIds.includes(o.id));
    if (doomed.length === 0) return;
    const approved = doomed.filter((o) => selectedOptionIds.includes(o.id)).length;
    const message = `Delete ${doomed.length === 1 ? `"${doomed[0].name || 'this option'}"` : `${doomed.length} options`}? ${approved > 0 ? 'An approved option is among them: it stops counting in the Budget once the selection is saved. ' : ''}This action cannot be undone.`;
    if (!window.confirm(message)) return;
    doomed.forEach((o) => removeOption(o.id));
    setSelectedOptionIds((prev) => prev.filter((id) => !doomed.some((o) => o.id === id)));
    exitSelecting();
  };

  // Builder Price of a tile: the total of the option's items (what the modal shows); until they are read, what is saved. Not given at all to a caller who may not see it.
  const builderPriceOf = (optionId: string): number | undefined => {
    if (!canViewBuilderPrice) return undefined;
    const items = itemsByOption[optionId];
    if (items) return linesTotal(items.lines);
    return Number(options.find((o) => o.id === optionId)?.builder_price || 0);
  };

  const thumbnailOf = (optionId: string) => staging.uploadedFor(optionId).find((file) => (file.file_type || '').startsWith('image/'));

  // Select / deselect from the form: auto-saved through the API, the server
  // decides what the siblings become (single vs multiple selection).
  const toggleSelected = async (optionId: string, selected: boolean) => {
    if (!initialData?.id || isTempId(optionId)) return;
    setTogglingOptionId(optionId);
    setError(null);
    try {
      const updated = await setOptionSelected(initialData.id, optionId, selected);
      setSelectedOptionIds(updated.selected_option_ids);
      onSelectionChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the selection');
    } finally {
      setTogglingOptionId(null);
    }
  };

  /**
   * Saves the Selection, its options and each option's own items through the API (the items after the options: they need their real ids). Returns the
   * Selection's id and how many staged files could not be uploaded; throws on the first failure (nothing is half-hidden).
   */
  const persist = async (data: Record<string, any>): Promise<{ selectionId: string; failedFiles: number }> => {
    const drafts: OptionDraft[] = options.map((o) => ({
      id: o.id,
      name: o.name,
      description: o.description,
      link: o.link,
      client_price: toMoney(o.client_price),
      // (no Builder Price: it is the total of the option's items, computed by the server)
    }));
    const result = await saveSelection({
      selectionId: mode === 'edit' ? initialData?.id : undefined,
      fields: {
        name: String(data.name).trim(),
        description: String(data.description || '').trim() || null,
        due_date: String(data.due_date || '').trim() || null,
        allowance: data.allowance === '' ? 0 : parseFloat(String(data.allowance).replace(/[^0-9.]/g, '')) || 0,
        project_id: data.project_id,
        accept_multiple_selections: data.accept_multiple_selections === true,
      },
      options: drafts,
      removedOptionIds,
    });
    // each option's items: only those that changed (an option nobody touched is not rewritten)
    for (const option of options) {
      const draft = itemsByOption[option.id];
      if (!draft || serializeItems(draft.lines) === draft.initial) continue;
      await saveSelectionOptionLines(result.optionIds[option.id], toLinePayload(draft.lines));
    }
    // The options now exist: persist the files that were waiting for them.
    const { failed } = await staging.uploadAllPending(result.optionIds);
    return { selectionId: result.selectionId, failedFiles: failed };
  };

  const handleSubmit = async (data: Record<string, any>) => {
    setError(null);
    if (!String(data.name || '').trim()) { setError('Title is required'); return; }
    if (!data.project_id) { setError('Project is required'); return; }
    const untitled = options.findIndex((o) => !o.name.trim());
    if (untitled !== -1) { setError(`Option Title is required (option ${untitled + 1})`); return; }

    setSaving(true);
    try {
      const { selectionId, failedFiles } = await persist(data);
      onSaved({ selectionId, failedFiles });
    } catch (err) {
      console.error('[SelectionForm] save failed', err);
      setError(err instanceof Error ? err.message : 'Failed to save selection');
    } finally {
      setSaving(false);
    }
  };

  // -- Create PO -------------------------------------------------------------------------------------------------------------------------------
  // A Selection counts as approved once it has a SELECTED option (its status is "completed"; the model has no other approval): the items of the selected
  // option(s) are what a purchase order is made from. No selected option, no items to copy, or a purchase order already made -> the button is off.
  const selectedItems = selectedOptionIds.flatMap((id) => itemsByOption[id]?.lines ?? []);
  const itemsLoaded = selectedOptionIds.every((id) => !!itemsByOption[id]);
  const createPurchaseOrderBlocker = (() => {
    if (mode !== 'edit' || !initialData?.id) return 'Save the selection first';
    if (!canViewBuilderPrice) return 'You cannot see the items of this selection';
    if (selectedOptionIds.length === 0) return 'Select an option first: only an approved selection can become a purchase order';
    if (!itemsLoaded) return 'Loading the items…';
    if (selectedItems.length === 0) return 'The selected option has no items to copy';
    if (linkedPurchaseOrder) return `This selection already has a purchase order (${linkedPurchaseOrder.number})`;
    return null;
  })();

  const optionsSnapshot = (list: EditableOption[]) => JSON.stringify(list.map((o) => [o.id, o.name, o.description, o.link, Number(o.builder_price || 0), Number(o.client_price || 0)]));

  const isDirty = () => {
    const values = currentValuesRef.current;
    const fieldsChanged = Object.keys(values).some((key) => String(values[key] ?? '') !== String((initialValues as Record<string, unknown>)[key] ?? ''));
    const itemsChanged = Object.values(itemsByOption).some((draft) => serializeItems(draft.lines) !== draft.initial);
    return fieldsChanged || removedOptionIds.length > 0 || optionsSnapshot(options) !== snapshotRef.current || itemsChanged;
  };

  // "Create PO": the Purchase Order form (the same one the Purchase Orders page uses) opens with a COPY of the selected option's items -- never the option's own
  // rows, which stay as they are. Anything unsaved is saved first (the items must exist); the order is saved already linked to the selection, and a selection
  // has at most one.
  const handleCreatePurchaseOrder = async () => {
    if (createPurchaseOrderBlocker || !initialData?.id) return;
    const values = { ...initialValues, ...currentValuesRef.current };
    if (isDirty()) {
      if (!String(values.name || '').trim() || !values.project_id) { setError('Enter a title and a project before creating the purchase order.'); return; }
      if (options.some((o) => isTempId(o.id))) { setError('Save the selection before creating the purchase order: it has new options.'); return; }
      setSaving(true);
      try {
        await persist(values);
        // what is on screen is now what is saved
        snapshotRef.current = optionsSnapshot(options);
        setRemovedOptionIds([]);
        setItemsByOption((prev) => Object.fromEntries(Object.entries(prev).map(([id, draft]) => [id, { ...draft, initial: serializeItems(draft.lines) }])));
        onSelectionChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save selection');
        return;
      } finally {
        setSaving(false);
      }
    }
    const copied = selectedItems.map((line) => ({ ...line, key: newLineKey() }));
    setPurchaseOrderStart({
      title: String(values.name).trim(),
      projectId: values.project_id,
      description: derivedDescription({ type: 'Selection', number: initialData.selection_number, title: values.name, description: values.description }), // its title, then its own Description
      vendorId: commonVendorOf(copied),
      lines: copied,
    });
  };

  const openOption = options.find((o) => o.id === openOptionId) ?? null;

  const additionalSections = () => (
    <>
      <SelectionOptionsEditor
        options={options}
        onAdd={addOption}
        onOpen={setOpenOptionId}
        selectedOptionIds={selectedOptionIds}
        onToggleSelected={toggleSelected}
        togglingOptionId={togglingOptionId}
        thumbnailOf={thumbnailOf}
        builderPriceOf={builderPriceOf}
        selection={{ active: selecting, checkedIds, onEnter: () => setSelecting(true), onExit: exitSelecting, onToggleChecked: toggleChecked, onCopy: copyChecked, onDelete: deleteChecked }}
      />
      {mode === 'edit' && initialData?.id && (
        <LinkedDocsSection
          emptyText="No documents are linked to this selection yet"
          hasUnsavedChanges={isDirty}
          docs={linkedPurchaseOrder ? [{
            id: linkedPurchaseOrder.id,
            type: 'Purchase Order',
            entityType: 'purchase_order',
            label: linkedPurchaseOrder.title ? `${linkedPurchaseOrder.number} — ${linkedPurchaseOrder.title}` : linkedPurchaseOrder.number,
            detail: [linkedPurchaseOrder.status ? statusLabel(linkedPurchaseOrder.status) : '', linkedPurchaseOrder.amount != null ? formatMoney(Number(linkedPurchaseOrder.amount)) : ''].filter(Boolean).join(' · '),
          }] : []}
        />
      )}
      <GenericCommentsSection
        comments={comments}
        onAddComment={addComment}
        onDeleteComment={deleteComment}
        mode={mode}
        audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
        placeholder={mode === 'create' ? 'Comments are available after creating the selection' : 'Add a comment...'}
      />
    </>
  );

  return (
    <>
      <GenericForm
        title="Selection"
        mode={mode}
        fields={fields}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isOpen={isOpen}
        additionalSections={additionalSections}
        error={error}
        submitButtonText="Save Changes"
        saving={saving}
        onFieldChange={handleFieldChange}
        footerActions={mode === 'edit' && initialData?.id ? (
          <StandardButton type="button" customVariant="white" onClick={handleCreatePurchaseOrder} disabled={saving || createPurchaseOrderBlocker !== null} title={createPurchaseOrderBlocker ?? undefined}>
            Create PO
          </StandardButton>
        ) : undefined}
      />
      {/* siblings of the form (not inside it): a submit inside them must never submit the Selection */}
      <SelectionOptionModal
        isOpen={isOpen && openOption !== null}
        option={openOption}
        items={openOptionId ? itemsByOption[openOptionId] ?? null : null}
        canViewBuilderPrice={canViewBuilderPrice}
        projectId={currentValuesRef.current.project_id || initialData?.project_id || ''}
        staging={staging}
        onApply={applyOption}
        onClose={closeOptionModal}
      />
      <PurchaseOrderCreateDialog
        isOpen={purchaseOrderStart !== null}
        onClose={() => setPurchaseOrderStart(null)}
        onCreated={(order) => {
          setPurchaseOrderStart(null);
          setLinkedPurchaseOrder({ id: order.id, number: order.poNumber, title: order.title, status: order.status, amount: order.amount });
          if (initialData?.id) void loadLinkedPurchaseOrder(initialData.id); // and what the database says: the link is persisted, not just remembered here
          onSelectionChanged();
        }}
        defaults={purchaseOrderStart ?? undefined}
        selectionId={initialData?.id}
      />
    </>
  );
}
